import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Center, Loader } from '@mantine/core';
import useAuth from '@hooks/use-auth';
import { CCN_ENABLED } from '@constants/features';
import { pageUrl } from '@constants/url';
import { CCN_HOME, isCcnSession } from '@utils/auth-session';
import { CCN_RETURN_TO_KEY } from '@/services/cube-sso';
import { PPJKProvider } from '@contexts/PPJKContext';
import { CompanyFeaturesProvider } from '@contexts/CompanyFeaturesContext';
import { PermissionsProvider } from '@contexts/PermissionsContext';
import { SidebarProvider } from '../contexts/sidebar-context';
import { useTheme } from '@contexts/ThemeContext';
import CcnTopBar from '@components/ccn/CcnTopBar';

/**
 * Shell for CCN (CUBEforall) sessions: everything under /ccn.
 *
 * It mounts the same data providers the manifest editor needs
 * (PPJK / company features / permissions / sidebar context) but none of the
 * dashboard chrome or guards, so a CCN user only ever sees the workspace.
 * Anyone without a session is sent to the CUBE callback page, which starts
 * the SSO and returns them to the URL they asked for.
 */
export default function CcnLayout() {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 100);
    return () => clearTimeout(timer);
  }, []);

  if (!CCN_ENABLED) {
    return <Navigate to="/auth" replace />;
  }

  if (loading) {
    return (
      <Center style={{ height: '100vh' }}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (!isAuthenticated) {
    try {
      sessionStorage.setItem(CCN_RETURN_TO_KEY, `${location.pathname}${location.search}`);
    } catch {
      // Private mode: the user simply lands on the inbox instead.
    }
    return <Navigate to={`${CCN_HOME}/callback`} replace />;
  }

  // A normal JARVIS user has no business here; give them their own app.
  if (!isCcnSession()) {
    return <Navigate to={pageUrl.docPabean} replace />;
  }

  return (
    <PPJKProvider>
      <CompanyFeaturesProvider>
        <PermissionsProvider>
          <SidebarProvider>
            <CcnShell>
              <Outlet />
            </CcnShell>
          </SidebarProvider>
        </PermissionsProvider>
      </CompanyFeaturesProvider>
    </PPJKProvider>
  );
}

function CcnShell({ children }: { children: React.ReactNode }) {
  const { colors } = useTheme();
  const location = useLocation();
  // The manifest editor paints its own full-height layout.
  const fullBleed = /\/ccn\/manifest\//.test(location.pathname);
  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: colors.background,
        backgroundImage: colors.backgroundGradient,
        backgroundAttachment: colors.backgroundGradient ? 'fixed' : undefined,
      }}
    >
      <CcnTopBar />
      <div style={{ padding: fullBleed ? 0 : 20 }}>{children}</div>
    </div>
  );
}
